import PropTypes from "prop-types";
import { getMuiIcon, sizeMapping } from "./iconMapping";

/**
 * FontAwesome-compatible component that renders MUI icons
 * This component provides backwards compatibility with the old FontAwesome usage
 * while internally using MUI icons.
 *
 * @param {Object} props
 * @param {string} props.fa - Icon name (e.g., "edit", "trash", "plus")
 * @param {boolean} props.far - Use regular variant (ignored, for compatibility)
 * @param {boolean} props.fab - Use brand variant (ignored, for compatibility)
 * @param {string} props.size - Size: "xs", "sm", "lg", "2x", "3x", "5x", "7x", "10x"
 * @param {boolean} props.ariaHidden - Whether to hide from screen readers
 * @param {string} props.color - Icon color
 * @param {Object} props.style - Additional styles
 * @param {string} props.className - Additional CSS classes
 */
const FontAwesome = (props) => {
  const { fa, size, ariaHidden, color, style, className } = props;

  const IconComponent = getMuiIcon(fa);

  if (!IconComponent) {
    // Fallback: render placeholder for unmapped icons
    return (
      <span
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: sizeMapping[size || ""] || 20,
          height: sizeMapping[size || ""] || 20,
          fontSize: sizeMapping[size || ""] || 20,
          color: color,
          ...style,
        }}
        aria-hidden={ariaHidden}
      >
        ?
      </span>
    );
  }

  const fontSize = sizeMapping[size || ""] || 20;

  return (
    <IconComponent
      className={className}
      sx={{
        fontSize: fontSize,
        color: color,
        verticalAlign: "middle",
        ...style,
      }}
      aria-hidden={ariaHidden}
    />
  );
};

FontAwesome.propTypes = {
  fa: PropTypes.string.isRequired,
  far: PropTypes.bool,
  fab: PropTypes.bool,
  size: PropTypes.oneOf([
    null,
    "xs",
    "sm",
    "lg",
    "1x",
    "2x",
    "3x",
    "5x",
    "7x",
    "10x",
  ]),
  ariaHidden: PropTypes.bool.isRequired,
  color: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
};

FontAwesome.defaultProps = {
  ariaHidden: true,
  far: false,
  fab: false,
};

export default FontAwesome;
